'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { Settings, Maximize, Minimize, Clock, Target, Trophy, RotateCcw, HelpCircle, ArrowLeft, Gamepad2, Lock, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@owlcat/ui';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { useGameTimer } from '@/contexts/games/hooks/useGameTimer';
import { usePathname, Link } from '@/lib/i18n/navigation';
import { gamesCatalog } from '@/lib/games-catalog';

type UseGameTimerReturn = ReturnType<typeof useGameTimer>;
import { cn } from '@/shared/utils/cn';

/**
 * In-header game navigation: "back to hub" + a switch-game dropdown. Lives
 * INSIDE the header bar (not a fixed overlay) so it never covers the game's own
 * controls. The current game is derived from the URL (next-intl usePathname is
 * locale-stripped → e.g. "/maze"), so no prop-threading through the game
 * components is needed.
 */
function GameNavInline({ isFullscreen }: { isFullscreen: boolean }) {
  const t = useTranslations('games');
  const pathname = usePathname();
  const currentId = pathname.replace(/^\/+/, '').split('/')[0];
  const others = gamesCatalog.filter((g) => g.id !== currentId);

  // In native fullscreen only the fullscreen element's subtree is painted, so a
  // menu portaled to document.body is invisible. Portal into the fullscreen
  // element instead (falls back to default body portal when not fullscreen).
  const portalContainer =
    isFullscreen && typeof document !== 'undefined'
      ? (document.fullscreenElement as HTMLElement | null)
      : undefined;

  return (
    <>
      {/* Back + switch-game live in the fleet SIDEBAR on desktop (lg+); here they only
          show below lg (mobile top bar has the hamburger, but a one-tap back is handier)
          and in native fullscreen, where the sidebar is not painted. */}
      <Button asChild variant="ghost" size="sm" className={cn("h-7 gap-1 px-1.5 sm:h-9 sm:px-3 shrink-0", !isFullscreen && "lg:hidden")} data-testid="game-back">
        <Link href="/">
          <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">{t('backToGames')}</span>
        </Link>
      </Button>
      {others.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className={cn("h-7 gap-1 px-1.5 sm:h-9 sm:px-3 shrink-0", !isFullscreen && "lg:hidden")} data-testid="game-switch" aria-label={t('switchGame')}>
              <Gamepad2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="hidden md:inline">{t('switchGame')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" portalContainer={portalContainer}>
            <DropdownMenuLabel>{t('switchGame')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {others.map((game) => (
              <DropdownMenuItem key={game.id} asChild>
                <Link href={game.href} className="cursor-pointer">
                  <span aria-hidden>{game.emoji}</span>
                  <span className="flex-1">{t(`${game.id}.name`)}</span>
                  {game.requiresAuth ? <Lock className="size-3.5 text-muted-foreground" aria-label={t('requiresSignIn')} /> : null}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

interface UniversalGameHeaderProps {
  title: string;
  isFullscreen: boolean;
  onFullscreenToggle: () => void;
  onSettingsToggle: () => void;
  onNewGame: () => void;
  onShowRules?: () => void;
  timer: UseGameTimerReturn;
  stats: {
    target: number;
    score: number;
    moves: number;
  };
  showRulesButton?: boolean;
  className?: string;
  extraActions?: React.ReactNode;
}

/**
 * Универсальный заголовок игры с интегрированным управлением
 * Может использоваться во всех играх платформы
 */
export function UniversalGameHeader({
  title,
  isFullscreen,
  onFullscreenToggle,
  onSettingsToggle,
  onNewGame,
  onShowRules,
  timer,
  stats,
  showRulesButton = true,
  className,
  extraActions
}: UniversalGameHeaderProps) {
  const t = useTranslations();

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-1 sm:gap-3 px-2 py-1.5 sm:p-3',
        'bg-card/90 dark:bg-card/95 backdrop-blur-md border-b border-border shadow-lg',
        'shrink-0',
        className
      )}
      data-testid="game-header"
    >
      {/* Левая часть - навигация (назад/сменить игру) + новая игра + заголовок */}
      <div className="flex items-center gap-1 sm:gap-2 min-w-0 overflow-hidden">
        <GameNavInline isFullscreen={isFullscreen} />
        <Button
          variant="default"
          size="sm"
          onClick={onNewGame}
          className="gap-1 bg-primary hover:bg-primary/90 px-1.5 sm:px-3 h-7 sm:h-9 shrink-0"
          data-testid="game-new-game-button"
        >
          <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">{t('games.newGame')}</span>
        </Button>
        <h1 className="text-xs sm:text-xl font-bold text-foreground truncate hidden sm:block" data-testid="game-title">{title}</h1>
      </div>

      {/* Центр - компактная статистика */}
      <div className="flex items-center gap-1 sm:gap-2 shrink-0" data-testid="game-stats">
        <Badge
          variant="secondary"
          className={cn(
            'gap-0.5 text-[10px] sm:text-sm px-1.5 sm:px-2.5 py-0 sm:py-0.5 transition-colors',
            timer.isRunning ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
          )}
          data-testid="game-timer-badge"
        >
          <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
          {timer.formattedTime}
        </Badge>

        <Badge
          variant="outline"
          className={cn(
            'gap-0.5 text-[10px] sm:text-sm px-1.5 sm:px-2.5 py-0 sm:py-0.5 transition-colors',
            stats.score >= stats.target ? 'bg-success/10 text-success border-success/30' : ''
          )}
          data-testid="game-score-badge"
        >
          <Trophy className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-warning-foreground dark:text-warning" />
          <span data-testid="game-score-value">{stats.score}/{stats.target}</span>
        </Badge>

        <Badge variant="outline" className="gap-0.5 text-[10px] sm:text-sm px-1.5 sm:px-2.5 py-0 sm:py-0.5" data-testid="game-moves-badge">
          <RotateCcw className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-primary" />
          <span data-testid="game-moves-value">{stats.moves}</span>
        </Badge>
      </div>

      {/* Правая часть - иконки действий */}
      <div className="flex items-center gap-0.5 sm:gap-1 shrink-0" data-testid="game-actions">
        {/* ≥ sm: every action inline. Phones: the game's own extras (solution /
            print / download) + rules fold into ONE "⋯" menu — the bar kept ten
            icons and nothing was findable. */}
        <div className="hidden sm:contents">{extraActions}</div>

        {showRulesButton && onShowRules && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onShowRules}
            className="hover:bg-accent h-7 w-7 sm:h-9 sm:w-auto sm:px-3 p-0 sm:gap-2 hidden sm:flex"
            data-testid="game-rules-button"
          >
            <HelpCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">{t('games.rules')}</span>
          </Button>
        )}

        {(extraActions || (showRulesButton && onShowRules)) && (
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="hover:bg-accent h-7 w-7 p-0" data-testid="game-more-button" aria-label={t('games.settings')}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" portalContainer={isFullscreen && typeof document !== 'undefined' ? (document.fullscreenElement as HTMLElement | null) : undefined} className="min-w-40">
                <div className="flex flex-col items-stretch gap-1 p-1 [&_button]:h-9 [&_button]:w-full [&_button]:justify-start [&_button]:px-2 [&_button_span.hidden]:inline!">
                  {extraActions}
                  {showRulesButton && onShowRules && (
                    <Button variant="ghost" size="sm" onClick={onShowRules} className="gap-2" data-testid="game-rules-button-mobile">
                      <HelpCircle className="h-4 w-4" />
                      {t('games.rules')}
                    </Button>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={onSettingsToggle}
          className="hover:bg-accent h-7 w-7 sm:h-9 sm:w-auto sm:px-3 p-0 sm:gap-2"
          data-testid="game-settings-button"
        >
          <Settings className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="hidden md:inline">{t('games.settings')}</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onFullscreenToggle}
          className="hover:bg-accent h-7 w-7 sm:h-9 sm:w-auto sm:px-3 p-0 sm:gap-2 hidden sm:flex"
          data-testid="game-header-fullscreen-button"
        >
          {isFullscreen ? (
            <>
              <Minimize className="h-4 w-4" />
              <span className="hidden md:inline">{t('games.exitFullscreen')}</span>
            </>
          ) : (
            <>
              <Maximize className="h-4 w-4" />
              <span className="hidden md:inline">{t('games.enterFullscreen')}</span>
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
